import React, { useEffect, useRef, useState } from 'react';
import Swipeable, {
  SwipeableProps,
} from 'react-native-gesture-handler/Swipeable';
import {
  Animated,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import {
  LIST_ITEM_SEPARATOR_HEIGHT,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';

// Taken using suggestion in this issue
// https://github.com/software-mansion/react-native-gesture-handler/issues/977
export class SwipeableWithInstantClose extends Swipeable {
  instantClose = () => {
    const { dragX, rowTranslation } = this.state;
    dragX.setValue(0);
    rowTranslation.setValue(0);
    this.setState({ rowState: Math.sign(0) });
  };
}

// Add slight delay to the touchable opacity press so it doesn't flash when scrolling
export const SCROLL_DELAY = 75;

export type ListOptionValue = number | string;

export interface ListOption<TMetadata = {}> {
  // represents the position of an item in a filtered list that includes only the keyboard-navigable items
  // i.e., the orderable items that users can interact with via keyboard shortcuts
  flatIndex?: number;
  // represents the position of an item in the entire list, including all items such as orderable items, carousels, headers, and any other elements
  fullIndex?: number;
  metadata?: TMetadata;
  subtitle?: string | React.ReactNode;
  title: string;
  value: ListOptionValue;
}

type StyleFuncType<TListOption, TStyle> = (
  option: TListOption,
  index?: number,
) => StyleProp<TStyle> | null;

type ViewStyleFuncType<TListOption> = StyleFuncType<TListOption, ViewStyle>;
type TextStyleFuncType<TListOption> = StyleFuncType<TListOption, TextStyle>;

export const LIST_ITEM_VERTICAL_PADDING = 16;

type ListActionType<TListOption> = (
  option: TListOption,
  progressAnimatedValue:
    | Animated.Value
    | Animated.AnimatedInterpolation<number>,
  dragAnimatedValue: Animated.AnimatedInterpolation<number>,
  swipeableRef: SwipeableWithInstantClose | null,
  index?: number,
) => React.ReactNode;

export type ListItemStyleType<TListOption> =
  | StyleProp<ViewStyle>
  | ViewStyleFuncType<TListOption>;

export type ListItemTextStyleType<TListOption> =
  | StyleProp<TextStyle>
  | TextStyleFuncType<TListOption>;

export type SwipeableListItemProps<TListOption> = Omit<
  SwipeableProps,
  | 'onSwipeableLeftWillOpen'
  | 'onSwipeableOpen'
  | 'onSwipeableRightWillOpen'
  | 'renderLeftActions'
  | 'renderRightActions'
> & {
  onSwipeableLeftWillOpen?: (
    option: TListOption,
    swipeableRef: SwipeableWithInstantClose | null,
  ) => void;
  onSwipeableOpen?: (
    option: TListOption,
    swipeableRef: SwipeableWithInstantClose | null,
  ) => void;
  onSwipeableRightWillOpen?: (
    option: TListOption,
    swipeableRef: SwipeableWithInstantClose | null,
  ) => void;
  renderLeftActions?: ListActionType<TListOption>;
  renderRightActions?: ListActionType<TListOption>;
};

export interface Props<TListOption> {
  columnCount?: number;
  disabled?: boolean;
  index?: number;
  item: TListOption;
  leftComponentStyle?: StyleProp<ViewStyle>;
  listItemHoveredStyle?: ListItemStyleType<TListOption>;
  listItemStyle?: ListItemStyleType<TListOption>;
  listTitleStyle?: ListItemTextStyleType<TListOption>;
  onSelect?: (option: TListOption) => void;
  optionListItemStyle?: StyleProp<ViewStyle>;
  renderLeftComponent?: (
    option: TListOption,
    isHovered?: boolean,
  ) => React.ReactNode;
  renderListItemFooter?: (
    option: TListOption,
    isHovered?: boolean,
  ) => React.ReactNode;
  renderListItemHeader?: (
    option: TListOption,
    columnCount?: number,
    isHovered?: boolean,
  ) => React.ReactNode;
  renderMainContent?: (
    option: TListOption,
    isHovered?: boolean,
  ) => React.ReactNode;
  renderRightComponent?: (
    option: TListOption,
    isHovered?: boolean,
  ) => React.ReactNode;
  rightComponentStyle?: StyleProp<ViewStyle>;
  subtitleMaxLines?: number;
  swipeableProps?: SwipeableListItemProps<TListOption>;
  testID?: string;
}

const SUBTITLE_MAX_LINES = 2;

interface MainContentProps<TListOption> {
  item: TListOption;
  listTitleStyle?: StyleProp<TextStyle>;
  subtitleMaxLines?: number;
}

export const DefaultMainContent = <
  TListOption extends ListOption = ListOption,
>({
  item,
  listTitleStyle,
  subtitleMaxLines = SUBTITLE_MAX_LINES,
}: MainContentProps<TListOption>) => {
  const [styles] = useStyles();

  // Render the subtitle only if it's a non-empty string or React node
  let subtitle = null;
  if (item.subtitle) {
    if (typeof item.subtitle === 'string') {
      subtitle = (
        <Text numberOfLines={subtitleMaxLines} style={TYPOGRAPHY.listSubtitle}>
          {item.subtitle}
        </Text>
      );
    } else {
      subtitle = item.subtitle;
    }
  }

  return (
    <View style={styles.content}>
      <Text numberOfLines={1} style={[TYPOGRAPHY.listTitle, listTitleStyle]}>
        {item.title}
      </Text>
      {subtitle}
    </View>
  );
};

export const ListItem = <TListOption extends ListOption = ListOption>({
  columnCount,
  disabled,
  index,
  item,
  leftComponentStyle,
  listItemStyle,
  listItemHoveredStyle,
  listTitleStyle,
  onSelect,
  optionListItemStyle,
  renderLeftComponent,
  renderListItemFooter,
  renderListItemHeader,
  renderMainContent,
  renderRightComponent,
  rightComponentStyle,
  subtitleMaxLines,
  swipeableProps,
  testID,
}: Props<TListOption>) => {
  const [styles] = useStyles();

  const swipeableRef = useRef<SwipeableWithInstantClose>(null);
  const swipeableOpenRef = useRef(false);
  const leftActions = swipeableProps?.renderLeftActions
    ? (
        progress: Animated.Value | Animated.AnimatedInterpolation<number>,
        drag: Animated.AnimatedInterpolation<number>,
      ) =>
        swipeableProps?.renderLeftActions?.(
          item,
          progress,
          drag,
          swipeableRef.current,
          index,
        )
    : undefined;
  const rightActions = swipeableProps?.renderRightActions
    ? (
        progress: Animated.Value | Animated.AnimatedInterpolation<number>,
        drag: Animated.AnimatedInterpolation<number>,
      ) =>
        swipeableProps?.renderRightActions?.(
          item,
          progress,
          drag,
          swipeableRef.current,
          index,
        )
    : undefined;

  // close the swipeable if the item value changes so recycled items are not
  // open when they become visible
  const lastItemValueRef = useRef(item.value);
  useEffect(() => {
    if (item.value !== lastItemValueRef.current) {
      swipeableRef.current?.instantClose();
      lastItemValueRef.current = item.value;
    }
  }, [item.value]);

  const [isHovered, setIsHovered] = useState(false);

  const itemStyle = listItemStyle
    ? typeof listItemStyle === 'function'
      ? listItemStyle(item, index)
      : listItemStyle
    : null;

  const itemHoveredStyle = listItemHoveredStyle
    ? typeof listItemHoveredStyle === 'function'
      ? listItemHoveredStyle(item, index)
      : listItemHoveredStyle
    : null;

  const titleStyle = listTitleStyle
    ? typeof listTitleStyle === 'function'
      ? listTitleStyle(item, index)
      : listTitleStyle
    : null;

  const leftComponent = renderLeftComponent?.(item, isHovered);
  const rightComponent = renderRightComponent?.(item, isHovered);

  const itemContent = (
    <View
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      style={[styles.mainRow, itemStyle, isHovered && itemHoveredStyle]}
    >
      {renderListItemHeader
        ? renderListItemHeader(item, columnCount, isHovered)
        : null}
      <View style={[styles.optionListItem, optionListItemStyle]}>
        {leftComponent ? (
          <View style={[styles.leftElementStyle, leftComponentStyle]}>
            {leftComponent}
          </View>
        ) : null}
        {renderMainContent ? (
          renderMainContent(item, isHovered)
        ) : (
          <DefaultMainContent
            item={item}
            listTitleStyle={titleStyle}
            subtitleMaxLines={subtitleMaxLines}
          />
        )}
        {rightComponent ? (
          <View style={[styles.rightElementStyle, rightComponentStyle]}>
            {rightComponent}
          </View>
        ) : null}
      </View>
      {renderListItemFooter ? renderListItemFooter(item, isHovered) : null}
    </View>
  );

  // itemContent will be wrapped by Swipeable only if swipeable props are passed.
  // This will prevent the list item from swallowing gestures it doesn't need to
  // listen to
  const contentWrapper = swipeableProps ? (
    <SwipeableWithInstantClose
      {...swipeableProps}
      onSwipeableClose={() => {
        swipeableOpenRef.current = false;
      }}
      onSwipeableLeftWillOpen={
        swipeableProps?.onSwipeableLeftWillOpen
          ? () => {
              swipeableOpenRef.current = true;
              swipeableProps.onSwipeableLeftWillOpen?.(
                item,
                swipeableRef.current,
              );
            }
          : undefined
      }
      onSwipeableOpen={
        swipeableProps?.onSwipeableOpen
          ? () => {
              swipeableOpenRef.current = true;
              swipeableProps.onSwipeableOpen?.(item, swipeableRef.current);
            }
          : undefined
      }
      onSwipeableRightWillOpen={
        swipeableProps?.onSwipeableRightWillOpen
          ? () => {
              swipeableOpenRef.current = true;
              swipeableProps?.onSwipeableRightWillOpen?.(
                item,
                swipeableRef.current,
              );
            }
          : undefined
      }
      onSwipeableWillClose={() => {
        swipeableOpenRef.current = false;
      }}
      ref={swipeableRef}
      renderLeftActions={leftActions}
      renderRightActions={rightActions}
    >
      {itemContent}
    </SwipeableWithInstantClose>
  ) : (
    itemContent
  );

  // TODO: make focusable when improving accessibility

  // Only wrap content in touchable if onSelect is passed. Otherwise it will
  // break any child touchables on web
  return (
    <View onStartShouldSetResponder={() => true} style={styles.base}>
      {onSelect ? (
        <TouchableOpacity
          {...(testID ? { testID } : null)}
          delayPressIn={SCROLL_DELAY}
          disabled={disabled}
          focusable={false}
          onPress={() => {
            if (!swipeableOpenRef.current) {
              onSelect(item);
            }
          }}
        >
          {contentWrapper}
        </TouchableOpacity>
      ) : (
        <View {...(testID ? { testID } : null)}>{contentWrapper}</View>
      )}
    </View>
  );
};

export const ListItemSeparator: React.FC<ViewProps> = ({ style, ...rest }) => {
  const [styles] = useStyles();
  return <View style={[styles.optionListItemSeparator, style]} {...rest} />;
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  leftElementStyle: {
    marginRight: 12,
  },
  mainRow: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: LIST_ITEM_VERTICAL_PADDING,
  },
  optionListItem: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  optionListItemSeparator: {
    backgroundColor: theme.colors.stroke,
    height: LIST_ITEM_SEPARATOR_HEIGHT,
    marginHorizontal: SCREEN_PADDING,
    // Ensure list item content overflow renders above the separator
    zIndex: -1,
  },
  rightElementStyle: {
    marginLeft: 12,
  },
}));
